import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

export type PublicViewerHostContextValue = {
  /**
   * The board the host has drilled INTO, or `null` when a page's own body is showing. This exists
   * ONLY for the data layer's board-scope selector (`useCurrentBoardIdSelector`), whose consumers
   * (useBoardSearchParams and the view-item hooks) also serve the PRIVATE app and so cannot read
   * the required public-only contracts. All navigation BEHAVIOR lives on the required contracts
   * (asset modal on PublicAssetModalNavigationProvider; drills on the board/collection trees' own
   * contracts).
   */
  navigation?: {
    activeBoardId: string | null;
  };
};

const PublicViewerHostContext = createContext<PublicViewerHostContextValue | null>(null);

/*
 * The last remainder of the public viewer's optional hosting bridge. It once carried the
 * collection link editor's composition bindings behind presence branches; those all live on
 * required contracts now — the asset modal on PublicAssetModalNavigationProvider, the collection
 * tree's composition (block rendering, card renderers, selection bar, document ownership, board
 * drills) on PublicShowcaseProvider.
 *
 * What is left is SOLELY the data layer's board-scope pointer for `useCurrentBoardIdSelector`:
 * its consumers (useBoardSearchParams and the view-item hooks) also serve the PRIVATE app, which
 * mounts neither public contract, so they cannot read a public-only required context. Hence this
 * stays deliberately optional (the hosting-bridge exception in rules/react-context.md): absence
 * means "the board id comes from the URL", the meaningful default everywhere but the editor's
 * Edit tab. Final dissolution — threading the board id through those consumers so this file can be
 * deleted — is tracked separately.
 */
export const usePublicViewerHost = (): PublicViewerHostContextValue | null => useContext(PublicViewerHostContext);

export type PublicViewerHostProviderProps = {
  children: ReactNode;
  navigation?: PublicViewerHostContextValue['navigation'];
};

/**
 * Carries the host's board-scope pointer into the public collection tree beneath it. The one
 * mount today is the collection link editor's Edit-tab canvas, whose drilled board lives in editor
 * state instead of a `/b/{boardId}` URL segment.
 */
export const PublicViewerHostProvider = memo(({ children, navigation }: PublicViewerHostProviderProps) => {
  const contextValue = useMemo<PublicViewerHostContextValue>(() => ({ navigation }), [navigation]);

  return <PublicViewerHostContext.Provider value={contextValue}>{children}</PublicViewerHostContext.Provider>;
});

PublicViewerHostProvider.displayName = 'PublicViewerHostProvider';
